import { ConnectedAccount, Integration, WithId } from '@superfunnel/types';
import {
  collection,
  doc,
  getDocs,
  query,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../Firebase';
import { emptyConverter } from '../utils';
import { getHubSpotUrl } from '../utils/auth';
import { removeUndefinedFields } from '../utils/firestore';

export const integrationsColRef = (accountId: string) =>
  collection(db, 'accounts', accountId, 'integrations').withConverter(
    emptyConverter<Integration>(),
  );

export const getIntegrations = async (
  accountId: string,
  dynamicPageId?: string,
) => {
  const fn = httpsCallable<
    { accountId: string; dynamicPageId?: string },
    WithId<Integration>[]
  >(functions, 'integrations/listIntegrations');

  const res = await fn({
    accountId,
    ...(dynamicPageId ? { dynamicPageId } : {}),
  });

  return res.data;
};

export const createWebhookIntegration = async (
  accountId: string,
  type: 'new_lead' | 'new_event' | 'new_partial_lead',
  name: string,
  url: string,
  objectMapping?: Record<string, any>[],
  extraHeaders?: Record<string, any>[],
  dynamicPageIds?: string[],
  formIds?: string[],
  authentication?: {
    type: 'apiKey';
    headerName: string;
    value: string;
  },
  eventTypes?: string[],
) => {
  const fn = httpsCallable<
    {
      accountId: string;
      type: 'new_lead' | 'new_event' | 'new_partial_lead';
      name: string;
      url: string;
      objectMapping?: Record<string, any>[];
      extraHeaders?: Record<string, any>[];
      dynamicPageIds?: string[];
      formIds?: string[];
      authentication?: {
        type: 'apiKey';
        headerName: string;
        value: string;
      };
      eventTypes?: string[];
    },
    WithId<Integration>
  >(functions, 'integrations/webhooks/createWebhook');

  const res = await fn({
    accountId,
    type,
    name,
    url,
    ...(objectMapping ? { objectMapping } : {}),
    ...(extraHeaders ? { extraHeaders } : {}),
    ...(dynamicPageIds ? { dynamicPageIds } : {}),
    ...(formIds ? { formIds } : {}),
    ...(authentication ? { authentication } : {}),
    ...(eventTypes ? { eventTypes } : {}),
  });

  return res.data;
};

export const updateIntegration = async (
  accountId: string,
  integrationId: string,
  update: any,
) => {
  return updateDoc(
    doc(integrationsColRef(accountId), integrationId),
    removeUndefinedFields(update, { undefinedTo: 'deleteField' }),
  );
};

export const duplicateWebhookIntegration = async (
  accountId: string,
  integrationId: string,
) => {
  const fn = httpsCallable<
    {
      accountId: string;
      integrationId: string;
    },
    WithId<Integration>
  >(functions, 'integrations/webhooks/duplicateWebhook');

  const res = await fn({
    accountId,
    integrationId,
  });

  return res.data;
};

// HubSpot Integration Functions

export interface HubSpotProperty {
  name: string;
  label: string;
  type: string;
  fieldType: string;
  groupName: string;
  description?: string;
  options?: Array<{
    label: string;
    value: string;
    displayOrder: number;
  }>;
}

export interface ConnectedAccountSummary {
  id: string;
  provider: string;
  name: string;
  status: 'active' | 'expired' | 'revoked';
  createdAt: any;
  lastUsedAt?: any;
  // Provider-specific
  hubspotPortalId?: string;
  /** Authentication type - 'oauth' or 'private_app' (defaults to 'oauth' for existing records) */
  authType?: 'oauth' | 'private_app';
}

/**
 * Get connected accounts collection reference
 */
export const connectedAccountsColRef = (accountId: string) =>
  collection(db, 'accounts', accountId, 'connectedAccounts').withConverter(
    emptyConverter<ConnectedAccount>(),
  );

/**
 * List connected accounts for an account (reads directly from Firestore)
 * Tokens are stored in a subcollection, so this is safe for client-side access
 */
export const listConnectedAccounts = async (
  accountId: string,
  provider?: string,
): Promise<ConnectedAccountSummary[]> => {
  const colRef = connectedAccountsColRef(accountId);

  const q = provider
    ? query(colRef, where('provider', '==', provider))
    : query(colRef);

  const snapshot = await getDocs(q);

  return snapshot.docs.map((docSnap) => {
    const data = docSnap.data();
    return {
      id: docSnap.id,
      provider: data.provider,
      name: data.name,
      status: data.status,
      createdAt: data.createdAt,
      lastUsedAt: data.lastUsedAt,
      // Provider-specific fields
      ...(data.provider === 'hubspot' && {
        hubspotPortalId: (data as any).hubspotPortalId,
        authType: (data as any).authType || 'oauth', // Default to 'oauth' for existing records
      }),
    };
  });
};

export const disconnectAccount = async (
  accountId: string,
  connectedAccountId: string,
  disableIntegrations = true,
) => {
  const fn = httpsCallable<
    {
      accountId: string;
      connectedAccountId: string;
      disableIntegrations?: boolean;
    },
    { success: boolean; message: string; disabledIntegrations: number }
  >(functions, 'integrations/connectedAccounts/disconnect');

  const res = await fn({ accountId, connectedAccountId, disableIntegrations });
  return res.data;
};

/**
 * Initiate HubSpot OAuth flow (client-side)
 * Creates an OAuth request document and returns the authorization URL
 */
export const initHubSpotOAuth = async (
  accountId: string,
  userId: string,
  from?: string,
) => {
  // Create OAuth request document for state management
  const oauthRequestRef = doc(
    collection(db, 'accounts', accountId, 'oauthRequests'),
  );

  await setDoc(oauthRequestRef, {
    requestId: oauthRequestRef.id,
    accountId,
    userId,
    provider: 'hubspot',
    from: from || '/integrations',
    createdAt: new Date(),
  });

  // Generate authorization URL with state
  const authUrl = getHubSpotUrl(oauthRequestRef.id);

  return {
    authUrl,
    requestId: oauthRequestRef.id,
  };
};

export const getHubSpotProperties = async (
  accountId: string,
  connectedAccountId: string,
) => {
  const fn = httpsCallable<
    { accountId: string; connectedAccountId: string },
    {
      properties: HubSpotProperty[];
      totalCount: number;
      filteredCount: number;
    }
  >(functions, 'integrations/hubspot/getProperties');

  const res = await fn({ accountId, connectedAccountId });
  return res.data;
};

export const createHubSpotIntegration = async (
  accountId: string,
  connectedAccountId: string,
  data: {
    name?: string;
    type?: 'new_lead' | 'new_event' | 'new_partial_lead';
    status?: 'active' | 'disabled' | 'draft';
    dynamicPageIds?: string[];
    formIds?: string[];
    fieldMappings?: Record<string, any>[];
    eventTypes?: string[];
  },
) => {
  const fn = httpsCallable<
    {
      accountId: string;
      connectedAccountId: string;
      name?: string;
      type?: 'new_lead' | 'new_event' | 'new_partial_lead';
      status?: 'active' | 'disabled' | 'draft';
      dynamicPageIds?: string[];
      formIds?: string[];
      fieldMappings?: Record<string, any>[];
      eventTypes?: string[];
    },
    any
  >(functions, 'integrations/hubspot/updateIntegration');

  const res = await fn({ accountId, connectedAccountId, ...data });
  return res.data;
};

export const updateHubSpotIntegration = async (
  accountId: string,
  integrationId: string,
  connectedAccountId: string,
  update: {
    name?: string;
    type?: 'new_lead' | 'new_event' | 'new_partial_lead';
    status?: 'active' | 'disabled';
    dynamicPageIds?: string[];
    formIds?: string[];
    fieldMappings?: Record<string, any>[];
    eventTypes?: string[];
  },
) => {
  const fn = httpsCallable<
    {
      accountId: string;
      integrationId: string;
      connectedAccountId: string;
      name?: string;
      type?: 'new_lead' | 'new_event' | 'new_partial_lead';
      status?: 'active' | 'disabled';
      dynamicPageIds?: string[];
      formIds?: string[];
      fieldMappings?: Record<string, any>[];
      eventTypes?: string[];
    },
    any
  >(functions, 'integrations/hubspot/updateIntegration');

  const res = await fn({
    accountId,
    integrationId,
    connectedAccountId,
    ...update,
  });
  return res.data;
};

// =============================================================================
// HubSpot Private App Functions
// =============================================================================

/**
 * Connect a HubSpot Private App using an access token
 *
 * Validates the token against HubSpot's API, retrieves the portal ID,
 * and creates a new connected account with the token stored securely.
 *
 * @see https://developers.hubspot.com/docs/api/private-apps
 */
export const connectHubSpotPrivateApp = async (
  accountId: string,
  accessToken: string,
  name: string,
): Promise<{
  success: boolean;
  connectedAccountId: string;
  portalId: string;
}> => {
  const fn = httpsCallable<
    {
      accountId: string;
      accessToken: string;
      name: string;
    },
    {
      success: boolean;
      connectedAccountId: string;
      portalId: string;
    }
  >(functions, 'integrations/hubspot/connectPrivateApp');

  const res = await fn({ accountId, accessToken, name });
  return res.data;
};

/**
 * Update the Private App token for an existing connected account
 *
 * Validates the new token against HubSpot's API to ensure it's valid
 * and belongs to the same portal, then updates the stored token.
 *
 * Use this when the user regenerates their Private App token in HubSpot.
 */
export const updateHubSpotPrivateAppToken = async (
  accountId: string,
  connectedAccountId: string,
  accessToken: string,
): Promise<{ success: boolean }> => {
  const fn = httpsCallable<
    {
      accountId: string;
      connectedAccountId: string;
      accessToken: string;
    },
    { success: boolean }
  >(functions, 'integrations/hubspot/updatePrivateAppToken');

  const res = await fn({ accountId, connectedAccountId, accessToken });
  return res.data;
};
